// Brand mark + wordmark. Inline SVG so it scales sharp at any DPR
// and inherits theme colors without needing a separate raster asset.
//
// Design language pulled from the autolecture chick logo: cream body,
// tan outline, navy text, with `<>` code brackets on top of the head
// and a navy play-triangle as the beak — the visual punch line is
// "code that becomes video".
//
// Variants:
//   <Brand />            — full mark + wordmark, default size 28px tall
//   <Brand wordmark={false} /> — just the chick mark
//   <Brand size={36} />  — bump glyph size; wordmark scales proportionally

import { Link } from 'react-router-dom';
import './Brand.css';

interface BrandProps {
  /** Mark height in px. Wordmark scales proportionally. */
  size?: number;
  /** Show wordmark next to the mark. */
  wordmark?: boolean;
  /** Wrap in a Link to `/` (default true). Pass false to render a span
      so callers can wrap with their own routing. */
  asLink?: boolean;
  /** Extra class on the wrapper. */
  className?: string;
}

export function Brand({
  size = 28,
  wordmark = true,
  asLink = true,
  className = '',
}: BrandProps) {
  const Wrapper: React.ElementType = asLink ? Link : 'span';
  const wrapperProps = asLink ? { to: '/' } : {};
  return (
    <Wrapper {...wrapperProps} className={`brand ${className}`.trim()}>
      <ChickMark size={size} />
      {wordmark && (
        <span className="brand-wordmark" style={{ fontSize: Math.round(size * 0.62) }}>
          autolecture
        </span>
      )}
    </Wrapper>
  );
}

// Standalone mark — kept exported so the eventual <link rel="icon">
// generator (or empty-state illustrations) can reuse the same SVG.
export function ChickMark({ size = 28 }: { size?: number }) {
  return (
    <svg
      className="brand-mark"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
    >
      {/* Body: cream blob with tan outline */}
      <ellipse
        cx="32" cy="33" rx="26" ry="25"
        fill="var(--brand-cream)"
        stroke="var(--brand-tan)"
        strokeWidth="2.5"
      />
      {/* Tuft on top of head — a small zig-zag */}
      <path
        d="M28 8 L32 4 L36 8"
        fill="none"
        stroke="var(--brand-tan)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Code brackets <> on the forehead */}
      <path
        d="M25 18 L20 22 L25 26"
        fill="none"
        stroke="var(--brand-tan)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M39 18 L44 22 L39 26"
        fill="none"
        stroke="var(--brand-tan)"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Eyes — black with white highlight */}
      <circle cx="22" cy="32" r="3.6" fill="#0a0a0a" />
      <circle cx="42" cy="32" r="3.6" fill="#0a0a0a" />
      <circle cx="23.2" cy="30.8" r="1" fill="#ffffff" />
      <circle cx="43.2" cy="30.8" r="1" fill="#ffffff" />
      {/* Cheek dot */}
      <circle cx="50" cy="40" r="1.8" fill="var(--brand-cheek)" />
      {/* Beak / play triangle — navy outline */}
      <path
        d="M28 39 L41 44.5 L28 50 Z"
        fill="none"
        stroke="var(--brand-navy)"
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
